import { useEffect, useState } from "react";
import { useFormContext } from "../../../../common/contexts/FormContext";
import { useFormConfigContext } from "../../../../common/contexts/FormConfigContext";
import { useSharedStateWithActions } from "../../../../common/hooks/useSharedState";
import {
  SESSION_SERVICE_API_ERROR,
  sharedStateKeys,
} from "../../../../constants/constant";
import {
  ContactUsApiCallHandler,
  fetchDynamicMenuJson,
  fetchLawnCareServices,
} from "../../../../service/apiService";
import IdentifyCountryCode from "../../../scheduler/components/Helper/IdentifyCountryCode";
import { getSessionAndCorrelationID } from "../../../../common/components/Helper/Helper";
import { sessionStorageUtils } from "../../../../utility/utility";

interface Service {
  id: number;
  name: string;
  cesname: string;
  serviceType: string;
  visibility: boolean;
  link: string;
  categoryName?: string;
  modifierName?: string;
  isPopular?: boolean;
  description?: string;
  externalId?: string;
  cesName?: string;
}

// Raw service data from API
interface ServiceData {
  id: number;
  name: string;
  cesname: string;
  serviceType: string;
  visibility: boolean;
  description?: string;
  isPopular?: boolean;
  link: string;
  externalId?: string;
  menu?: ServiceData[];
  category?: ServiceData[];
}

interface ServiceOption extends ServiceData {
  category?: ServiceData[];
}

type ServiceRawData = ServiceData | ServiceOption;

interface TrendingServiceGroup {
  trendingServiceType: "Popular" | "Other";
  services: {
    id: number;
    name: string;
    cesName: string;
    description: string;
    externalId: string;
    link: string;
  }[];
}

export const useServiceSelector = () => {
  const { formValues, setFormValues } = useFormContext();
  const { formConfig } = useFormConfigContext();
  const { state, setValue } = useSharedStateWithActions();
  const [services, setServices] = useState<Service[]>([]);
  const [selectedServices, setSelectedServices] = useState<number[]>(
    formValues["service-selection"] || [],
  );
  const [activeTab, setActiveTab] = useState<number>(
    formValues.serviceSelectorActiveTab || 0,
  );
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedServiceInfo, setSelectedServiceInfo] =
    useState<Service | null>(null);

  const zipCode = formValues.zipcode;
  const countryCode = IdentifyCountryCode(zipCode);

  const addService = (
    services: Service[],
    item: ServiceRawData,
    categoryName?: string,
    modifierName?: string,
  ) => {
    if (item.serviceType === "Service Sku" && item.visibility) {
      services.push({
        id: item.id,
        name: item.name,
        cesname: item.cesname,
        serviceType: item.serviceType || "Service Sku",
        visibility: item.visibility,
        link: item.link || "",
        description: item.description || "",
        isPopular: item.isPopular || false,
        categoryName,
        modifierName,
      });
    }
  };

  const processCategoryMenu = (
    services: Service[],
    category: ServiceData,
    categoryName: string,
    modifierName?: string,
  ) => {
    if (!Array.isArray(category.menu)) return;

    for (const menuItem of category.menu) {
      addService(services, menuItem, categoryName, modifierName);
    }
  };

  const processCategory = (
    services: Service[],
    category: ServiceData,
    modifierName?: string,
  ) => {
    const categoryName = category.name;
    addService(services, category, categoryName, modifierName);

    if (category.serviceType === "Service WebCategory" && category.visibility) {
      processCategoryMenu(services, category, categoryName, modifierName);
    }
  };

  const processServiceModifier = (
    services: Service[],
    option: ServiceOption,
    modifierName: string,
  ) => {
    if (!Array.isArray(option.category)) return;

    for (const category of option.category) {
      processCategory(services, category, modifierName);
    }
  };

  const processWebCategoryMenu = (
    services: Service[],
    option: ServiceOption,
    modifierName: string,
  ) => {
    if (!Array.isArray(option.menu)) return;

    for (const menuItem of option.menu) {
      addService(services, menuItem, option.name, modifierName);
    }
  };

  const processOption = (services: Service[], option: ServiceOption) => {
    const modifierName = option.name;
    addService(services, option, undefined, modifierName);

    if (option.serviceType === "Service Modifier") {
      processServiceModifier(services, option, modifierName);
    } else if (
      option.serviceType === "Service WebCategory" &&
      option.visibility
    ) {
      processWebCategoryMenu(services, option, modifierName);
    }
  };

  // Optimized service extraction logic
  const extractServices = (data: ServiceRawData[]): Service[] => {
    const services: Service[] = [];

    if (Array.isArray(data)) {
      for (const item of data) {
        processOption(services, item);
      }
    }

    return services;
  };

  const extractHighlightedText = (description?: string): string => {
    if (!description) return "";
    try {
      const spanRegex =
        /<span[^>]*class="[^"]*message[^"]*"[^>]*>(.*?)<\/span>/gi;
      const spanMatches = description.match(spanRegex);
      if (spanMatches) {
        const highlightedTexts = spanMatches.map((spanTag) => {
          return spanTag.replaceAll(/<span[^>]*>(.*?)<\/span>/gi, "$1");
        });
        return highlightedTexts.join(", ");
      }
      return "";
    } catch (error) {
      console.error("Error extracting highlighted text:", error);
      return "";
    }
  };

  // Add this helper function to remove span tags from description
  const removeMessageSpans = (description?: string): string => {
    if (!description) return "";
    return description.replaceAll(
      /<span[^>]*class="(?:[^"]*\s)?message(?:\s[^"]*)?"[^>]*>.*?<\/span>/gi,
      "",
    );
  };
  const handleServiceToggle = (serviceId: number) => {
    const updatedServices = selectedServices.includes(serviceId)
      ? selectedServices.filter((id) => id !== serviceId)
      : [...selectedServices, serviceId];

    setSelectedServices(updatedServices);

    // Store selected services in formValues
    const selectedServiceDetails = services.filter((service) =>
      updatedServices.includes(service.id),
    );

    const updatedServiceDetails = selectedServiceDetails.map((service) => {
      const highLightText = extractHighlightedText(service.description);
      const plainDescription = removeMessageSpans(service.description);

      return { ...service, highLightText, description: plainDescription };
    });
    setFormValues({
      ...formValues,
      "service-selection": updatedServices,
      selectedServiceDetails: updatedServiceDetails,
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setFormValues({
      ...formValues,
      serviceSelectorActiveTab: newValue,
    });
  };

  const handleInfoClick = (event: React.MouseEvent, service: Service) => {
    event?.stopPropagation();
    const updateService = removeMessageSpans(service.description);
    const serviceWithPlainDescription = {
      ...service,
      description: updateService,
    };
    setSelectedServiceInfo(serviceWithPlainDescription);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedServiceInfo(null);
  };

  const processLapServices = (data: TrendingServiceGroup[]): Service[] => {
    const services: Service[] = [];

    if (Array.isArray(data)) {
      for (const group of data) {
        const isPopular = group.trendingServiceType === "Popular";

        if (Array.isArray(group.services)) {
          for (const service of group.services) {
            services.push({
              id: service.id,
              name: service.name,
              cesname: service.cesName,
              serviceType: "Service Sku",
              visibility: true,
              link: service.link || "",
              description: service.description || "",
              externalId: service.externalId || "",
              isPopular: isPopular,
              categoryName: "",
              modifierName: "",
            });
          }
        }
      }
    }
    return services;
  };

  useEffect(() => {
    if (
      formValues.selectedServices &&
      Array.isArray(formValues.selectedServices)
    ) {
      setSelectedServices(formValues.selectedServices);
    }
  }, [formValues.selectedServices]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setValue(sharedStateKeys.IS_TRENDING_SERVICE_READY, false);
        sessionStorageUtils.removeItem(SESSION_SERVICE_API_ERROR);
        if (formValues.is_enable_lap_service) {
          const result = await fetchLawnCareServices({
            webLocationId: formValues.franchiseWebLocationId,
            dbaName: formValues.dbaName,
            conceptId: formValues.conceptID,
            serviceModifier: formValues.servicetype || "residential",
          });
          const extractedServices = processLapServices(
            result as TrendingServiceGroup[],
          );
          if (extractedServices.length === 0) {
            const sessionAndCorrelation = getSessionAndCorrelationID();
            const correlationID = sessionAndCorrelation.correlationID;
            sessionStorageUtils.setItem(SESSION_SERVICE_API_ERROR, true);
            await ContactUsApiCallHandler(
              formValues,
              formConfig,
              correlationID,
            );
          } else {
            setServices(extractedServices);
          }
          setLoading(false);
          return;
        }
        const result = await fetchDynamicMenuJson(
          formValues,
          state?.[sharedStateKeys.MULTIPLE_DOMAINS],
          countryCode,
        );

        const extractedServices = extractServices(result);

        if (extractedServices.length === 0) {
          const sessionAndCorrelation = getSessionAndCorrelationID();
          const correlationID = sessionAndCorrelation.correlationID;
          sessionStorageUtils.setItem(SESSION_SERVICE_API_ERROR, true);
          await ContactUsApiCallHandler(formValues, formConfig, correlationID);
        }

        const filterServices = (services: Service[]): Service[] => {
          return services.filter((service) => service.visibility);
        };

        const filteredServices = filterServices(extractedServices);
        setServices(filteredServices);
      } catch (error) {
        console.error("Error fetching service data:", error);
        try {
          const sessionAndCorrelation = getSessionAndCorrelationID();
          const correlationID = sessionAndCorrelation.correlationID;
          sessionStorageUtils.setItem(SESSION_SERVICE_API_ERROR, true);
          await ContactUsApiCallHandler(formValues, formConfig, correlationID);
        } catch (fallbackError) {
          console.error("Fallback service fetch also failed:", fallbackError);
          setServices([]);
        }
      } finally {
        setLoading(false);
        setValue(sharedStateKeys.IS_TRENDING_SERVICE_READY, true);
      }
    };

    if (formValues.franchiseWebLocationId) {
      fetchData();
    }
  }, [
    formValues.franchiseWebLocationId,
    formValues.is_enable_lap_service, // Add this dependency
    formValues.dbaName,
    formValues.conceptId,
    formValues.serviceModifier,
    state?.[sharedStateKeys.MULTIPLE_DOMAINS],
    countryCode,
  ]);

  // Computed values
  const popularServices = services.filter((service) => {
    const isPopular = service.isPopular === true;
    return isPopular;
  });

  const otherServices = services.filter((service) => {
    const isOther = service.isPopular !== true;
    return isOther;
  });

  const displayServices = activeTab === 0 ? popularServices : otherServices;
  const hasPopularServices = popularServices.length > 0;

  return {
    // State
    services,
    selectedServices,
    activeTab,
    loading,
    modalOpen,
    selectedServiceInfo,

    // Computed values
    popularServices,
    otherServices,
    displayServices,
    hasPopularServices,

    // Handlers
    handleServiceToggle,
    handleTabChange,
    handleInfoClick,
    handleModalClose,

    // Utilities
    extractServices,
  };
};

export type { Service };
